import { type FC, useState } from 'react';
import { Input, Button } from '@/components/ui';
import { Tag, X, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import type { DiscountInfo } from '@/lib/utils/discountCode';

export type { DiscountInfo };

export interface DiscountCodeInputProps {
  onApply: (code: string) => void | Promise<void>;
  onRemove: () => void;
  appliedCode: string | undefined;
  appliedDiscount: DiscountInfo | undefined;
  isLoading?: boolean;
}

/**
 * Component for entering and applying discount codes
 * Validates codes with Stripe and displays discount information
 */
export const DiscountCodeInput: FC<DiscountCodeInputProps> = ({
  onApply,
  onRemove,
  appliedCode,
  appliedDiscount,
  isLoading = false,
}) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleApply = () => {
    const trimmedCode = code.trim();
    
    if (!trimmedCode) {
      setError('Please enter a discount code');
      return;
    }

    setError(null);
    onApply(trimmedCode);
  };

  const handleRemove = () => {
    setCode('');
    setError(null);
    onRemove();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !appliedCode) {
      e.preventDefault();
      handleApply();
    }
  };

  // If a discount is already applied, show the applied state
  if (appliedCode && appliedDiscount) {
    return (
      <div className="space-y-2" data-testid="discount-code-applied" role="status" aria-live="polite">
        <div className="flex items-center gap-2 text-sm text-green-300">
          <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
          <span className="font-medium">Discount code applied</span>
        </div>
        <div className="bg-green-500/20 border border-green-500/40 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Tag className="h-4 w-4 text-green-300" aria-hidden="true" />
            <div>
              <p className="font-medium text-white text-sm">{appliedCode}</p>
              {appliedDiscount.description && (
                <p className="text-xs text-green-200">{appliedDiscount.description}</p>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRemove}
            disabled={isLoading}
            className="text-green-200 hover:text-white hover:bg-green-500/20"
            data-testid="remove-discount-button"
            aria-label={`Remove discount code ${appliedCode}`}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </div>
    );
  }

  // Otherwise, show the input form
  return (
    <div className="space-y-2" data-testid="discount-code-input">
      <label htmlFor="discount-code" className="text-sm font-medium text-orange-200">
        Discount Code (Optional)
      </label>
      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          id="discount-code"
          type="text"
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
            setError(null);
          }}
          onKeyDown={handleKeyDown}
          placeholder="Enter code"
          disabled={isLoading}
          className="flex-1"
          data-testid="discount-code-field"
          aria-invalid={!!error}
          aria-describedby={error ? 'discount-error' : undefined}
        />
        <Button
          onClick={handleApply}
          disabled={isLoading || !code.trim()}
          variant="outline"
          className="border-orange-500 text-orange-200 hover:bg-orange-500/20 w-full sm:w-auto"
          data-testid="apply-discount-button"
          aria-label="Apply discount code"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />
              <span className="hidden sm:inline">Validating...</span>
              <span className="sm:hidden">Checking...</span>
            </>
          ) : (
            'Apply'
          )}
        </Button>
      </div>
      {error && (
        <div 
          id="discount-error"
          className="flex items-start gap-2 text-sm text-red-300" 
          data-testid="discount-error"
          role="alert"
          aria-live="assertive"
        >
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};
